import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";
import { SC_LONG_TIMEOUT_MS, fmt, fmtPeriod, labelOf, scMessage } from "./scUtils";

const U = OPS.sc.uploadDialog;
const MAX_FILES = 6;                       // POST /sc/costs/sets takes 1–6 staging ids
const MAX_SENT_BYTES = 25 * 1024 * 1024;   // the server's limit, on the bytes as sent
const ACCEPT = ".xls,.html,.htm";
const BASES = ["last", "purchase", "median", "manual"];
const BUSY = ["compressing", "uploading"];

const num = (v, lang) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(v || 0);

function fmtSize(bytes, lang) {
    const mb = (bytes || 0) / (1024 * 1024);
    const nf = new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US", { maximumFractionDigits: 1 });
    return mb >= 1 ? `${nf.format(mb)} MB` : `${nf.format((bytes || 0) / 1024)} KB`;
}

/** The ERP export is a ~10 MB HTML table that compresses ~10×: gzip it in the
 *  browser when CompressionStream exists (sent as name + ".gz"; the parser
 *  reads gzip by its magic bytes), else send it raw. */
async function gzipped(file) {
    if (typeof CompressionStream === "undefined" || typeof file.stream !== "function") return file;
    try {
        const blob = await new Response(file.stream().pipeThrough(new CompressionStream("gzip"))).blob();
        return new File([blob], `${file.name}.gz`, { type: "application/gzip" });
    } catch {
        return file;
    }
}

/** Coverage of a period's valuation: with / without cost, by basis, and the
 *  items still without a cost (`onOpenItem` turns them into links). */
export function CoverageView({ coverage, bases, onOpenItem, testId }) {
    const { t, lang } = useI18n();
    if (!coverage) return null;
    const missing = coverage.without_cost || [];
    const total = coverage.items_total || 0;
    const pct = total ? Math.round(((coverage.with_cost || 0) / total) * 100) : 0;
    const byBasis = coverage.by_basis || {};
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
                <div className="min-w-0 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] px-2.5 py-2">
                    <p className="text-[10px] font-bold text-[color:var(--ink-muted)]">{t("stk_costs_with_cost")}</p>
                    <p data-value={coverage.with_cost ?? 0} className="mt-1 truncate text-sm font-extrabold text-[color:var(--ink)]">
                        <bdi dir="ltr">{num(coverage.with_cost, lang)}/{num(total, lang)}</bdi>
                        <span className="ms-1 text-[11px] font-semibold text-[color:var(--ink-muted)]"><bdi dir="ltr">{num(pct, lang)}%</bdi></span>
                    </p>
                </div>
                <div className={`min-w-0 rounded-[var(--radius-md)] border px-2.5 py-2 ${missing.length
                    ? "border-[color:var(--warning-soft-fg)]/30 bg-[color:var(--warning-soft)]" : "border-[color:var(--border-soft)]"}`}>
                    <p className="text-[10px] font-bold text-[color:var(--ink-muted)]">{t("stk_costs_without_cost")}</p>
                    <p data-value={missing.length} className="mt-1 truncate text-sm font-extrabold text-[color:var(--ink)]">
                        <bdi dir="ltr">{num(missing.length, lang)}</bdi>
                    </p>
                </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {BASES.filter((k) => byBasis[k]).map((k) => (
                    <span key={k} className="rounded-full bg-[color:var(--bg-surface-2)] px-2.5 py-1 text-[11px] font-semibold text-[color:var(--ink-muted)]">
                        {labelOf(bases, k, lang)}: <bdi dir="ltr" className="text-[color:var(--ink)]">{num(byBasis[k], lang)}</bdi>
                    </span>
                ))}
            </div>
            {missing.length ? (
                <div className="space-y-1.5">
                    <p className="text-xs font-bold text-[color:var(--ink-muted)]">{t("stk_costs_missing_title")}</p>
                    <ul data-testid={testId} className="max-h-64 divide-y divide-[color:var(--border-soft)] overflow-y-auto rounded-[var(--radius-md)] border border-[color:var(--border-soft)] scrollbar-thin">
                        {missing.map((i) => {
                            const body = (
                                <>
                                    <span className="block truncate text-sm font-semibold text-[color:var(--ink)]">{i.name_ar}</span>
                                    <span className="block text-[11px] text-[color:var(--ink-faint)]">
                                        <bdi dir="ltr">{i.code}</bdi> · {i.erp_code ? <bdi dir="ltr">{i.erp_code}</bdi> : t("stk_costs_no_erp_code")} · {i.count_unit}
                                    </span>
                                </>
                            );
                            return (
                                <li key={i.id} data-item-id={i.id}>
                                    {onOpenItem ? (
                                        <button type="button" onClick={() => onOpenItem(i)}
                                                className="block w-full px-3 py-2 text-start transition-[background-color] duration-150 hover:bg-[color:var(--brand-orange-soft)]">
                                            {body}
                                        </button>
                                    ) : <div className="px-3 py-2">{body}</div>}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : (
                <p className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--success)]">
                    <CheckCircle2 className="h-4 w-4" />{t("stk_costs_all_covered")}
                </p>
            )}
        </div>
    );
}

function FileRow({ f, index, onRemove, locked }) {
    const { t, lang } = useI18n();
    const r = f.result;
    const busy = BUSY.includes(f.status);
    return (
        <li data-testid={U.file(index)} data-status={f.status}
            className="space-y-1 rounded-[var(--radius-md)] border border-[color:var(--border-soft)] px-3 py-2">
            <div className="flex items-center gap-2">
                {busy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[color:var(--brand-orange)]" />
                    : f.status === "done" ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[color:var(--success)]" />
                        : f.status === "error" ? <AlertTriangle className="h-4 w-4 shrink-0 text-[color:var(--danger)]" />
                            : <FileSpreadsheet className="h-4 w-4 shrink-0 text-[color:var(--ink-faint)]" />}
                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-[color:var(--ink)]" dir="ltr">{f.file.name}</span>
                <span className="shrink-0 text-[11px] text-[color:var(--ink-faint)]"><bdi dir="ltr">{fmtSize(f.file.size, lang)}</bdi></span>
                {!busy && !locked ? (
                    <button type="button" onClick={() => onRemove(f.key)} aria-label={t("stk_costs_remove_file")}
                            data-testid={OPS.sc.uploadDialog.remove(index)}
                            className="rounded-full p-1 text-[color:var(--ink-faint)] transition-colors hover:text-[color:var(--ink)]">
                        <X className="h-4 w-4" />
                    </button>
                ) : null}
            </div>
            <p className="text-[11px] text-[color:var(--ink-muted)]">
                {f.status === "done" && r ? (
                    <>
                        <bdi dir="ltr">{r.date_from} – {r.date_to}</bdi>
                        {" · "}{fmt(t("stk_costs_file_rows"), { n: num(r.rows, lang) })}
                        {" · "}{fmt(t("stk_costs_file_items"), { n: num(r.items_n, lang) })}
                        {r.skipped_rows ? <> · {fmt(t("stk_costs_file_skipped"), { n: num(r.skipped_rows, lang) })}</> : null}
                    </>
                ) : f.status === "error" ? (
                    <span className="font-semibold text-[color:var(--danger)]">{f.error}</span>
                ) : t(`stk_costs_status_${f.status}`)}
            </p>
        </li>
    );
}

/** «رفع ملف التكاليف»: one or more ERP «Item Movement Card» exports, staged
 *  one file at a time (POST /sc/costs/files, per-file status), then «اعتماد»
 *  turns the staged files into one cost set (POST /sc/costs/sets) and shows
 *  its coverage. */
export function CostUploadDialog({ open, onOpenChange, scope, onCommitted }) {
    const { t, lang } = useI18n();
    const [files, setFiles] = useState([]);   // [{key, file, status, result, error}]
    const [note, setNote] = useState("");
    const [committing, setCommitting] = useState(false);
    const [committed, setCommitted] = useState(null);   // {set, coverage}
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);
    const abortRef = useRef(null);
    const seq = useRef(0);

    useEffect(() => {
        if (open) {
            setFiles([]); setNote(""); setCommitted(null); setCommitting(false);
        } else if (abortRef.current) {
            abortRef.current.abort();
        }
    }, [open]);
    useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

    const patch = (key, p) => setFiles((list) => list.map((f) => (f.key === key ? { ...f, ...p } : f)));

    const upload = async (entry) => {
        const ctl = new AbortController();
        abortRef.current = ctl;
        patch(entry.key, { status: "compressing" });
        try {
            const sent = await gzipped(entry.file);
            if (ctl.signal.aborted) return;
            if (sent.size > MAX_SENT_BYTES) {
                patch(entry.key, { status: "error", error: t("stk_costs_file_too_large") });
                return;
            }
            patch(entry.key, { status: "uploading" });
            const fd = new FormData();
            fd.append("file", sent, sent.name);
            const r = await api.post("/sc/costs/files", fd, { timeout: SC_LONG_TIMEOUT_MS, signal: ctl.signal });
            patch(entry.key, { status: "done", result: r.data });
        } catch (e) {
            if (ctl.signal.aborted) return;
            patch(entry.key, { status: "error", error: scMessage(e, lang) || t("stk_costs_upload_failed") });
        } finally {
            if (abortRef.current === ctl) abortRef.current = null;
        }
    };

    // the queue: one file in flight at a time, the next queued one starts when it settles
    useEffect(() => {
        if (!open || files.some((f) => BUSY.includes(f.status))) return;
        const next = files.find((f) => f.status === "queued");
        if (next) upload(next);
    }, [files, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const add = (list) => {
        const picked = Array.from(list || []).filter((f) => /\.(xls|html?)$/i.test(f.name));
        if (!picked.length) { toast.error(t("stk_costs_wrong_type")); return; }
        const room = Math.max(MAX_FILES - files.length, 0);
        if (picked.length > room) toast.error(fmt(t("stk_costs_max_files"), { n: MAX_FILES }));
        const entries = picked.slice(0, room).map((file) => {
            seq.current += 1;
            return { key: `f${seq.current}`, file, status: "queued", result: null, error: null };
        });
        if (entries.length) setFiles((cur) => [...cur, ...entries]);
    };
    const remove = (key) => setFiles((cur) => cur.filter((f) => f.key !== key));

    const done = files.filter((f) => f.status === "done");
    const pending = files.some((f) => f.status === "queued" || BUSY.includes(f.status));
    const canCommit = !!done.length && !pending && !committing && !committed;
    // the set's period is the month of the latest «to» date across the files
    const lastTo = done.map((f) => f.result?.date_to || "").sort().pop();
    const period = lastTo ? lastTo.slice(0, 7) : "";

    const commit = async () => {
        setCommitting(true);
        try {
            const r = await api.post("/sc/costs/sets", {
                staging_ids: done.map((f) => f.result.staging_id), note: note.trim() || undefined,
            }, { timeout: SC_LONG_TIMEOUT_MS });
            setCommitted(r.data);
            toast.success(t("stk_costs_committed"));
            onCommitted && onCommitted(r.data);
        } catch (e) {
            toast.error(scMessage(e, lang) || t("stk_admin_offline_error"));
        } finally {
            setCommitting(false);
        }
    };

    // closing mid-upload aborts the request (a staged file expires on its own); never mid-commit
    const blockClose = committing;
    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && blockClose) return; onOpenChange(o); }}>
            <DialogContent data-testid={U.dialog} className="max-h-[88vh] max-w-lg overflow-y-auto bg-[color:var(--bg-surface)] scrollbar-thin">
                <DialogHeader className="text-start sm:text-start">
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5 text-[color:var(--brand-orange)]" />{t("stk_costs_upload_title")}
                    </DialogTitle>
                    <DialogDescription>{t("stk_costs_upload_body")}</DialogDescription>
                </DialogHeader>

                {committed ? (
                    <div data-testid={U.result} className="space-y-3">
                        <p className="flex items-center gap-2 text-sm font-semibold text-[color:var(--success)]">
                            <CheckCircle2 className="h-4 w-4" />
                            {fmt(t("stk_costs_committed_for"), { period: fmtPeriod(committed.set?.period, lang) })}
                        </p>
                        <CoverageView coverage={committed.coverage} bases={scope?.labels?.bases} />
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div data-testid={U.drop} role="button" tabIndex={0}
                             onClick={() => inputRef.current?.click()}
                             onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
                             onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                             onDragLeave={() => setDragging(false)}
                             onDrop={(e) => { e.preventDefault(); setDragging(false); add(e.dataTransfer?.files); }}
                             className={`flex cursor-pointer items-center gap-3 rounded-[var(--radius-lg)] border border-dashed p-4 transition-colors duration-150 ${dragging
                                 ? "border-[color:var(--brand-orange)] bg-[color:var(--brand-orange-soft)]"
                                 : "border-[color:var(--border-strong)] bg-[color:var(--bg-surface-2)]"}`}>
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-[color:var(--bg-surface)]">
                                <Upload className="h-5 w-5 text-[color:var(--brand-brown)]" />
                            </span>
                            <span className="min-w-0">
                                <span className="block text-sm font-semibold text-[color:var(--ink)]">{t("stk_costs_choose")}</span>
                                <span className="block text-[11px] text-[color:var(--ink-muted)]">
                                    {fmt(t("stk_costs_choose_hint"), { n: MAX_FILES })} · <bdi dir="ltr">.xls · .html</bdi>
                                </span>
                            </span>
                            <input ref={inputRef} data-testid={U.input} type="file" multiple accept={ACCEPT} className="hidden"
                                   onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
                        </div>

                        {files.length ? (
                            <ul className="space-y-2">
                                {files.map((f, i) => <FileRow key={f.key} f={f} index={i} onRemove={remove} locked={committing} />)}
                            </ul>
                        ) : null}

                        {done.length ? (
                            <>
                                {period ? (
                                    <p className="text-xs font-semibold text-[color:var(--ink)]">
                                        {fmt(t("stk_costs_will_be_for"), { period: fmtPeriod(period, lang) })}
                                    </p>
                                ) : null}
                                <label className="block space-y-1 text-xs font-bold text-[color:var(--ink)]">
                                    <span>{t("stk_costs_note")}</span>
                                    <Input value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} className="h-11 text-base" />
                                </label>
                            </>
                        ) : null}
                    </div>
                )}

                <DialogFooter className="gap-2 sm:gap-2">
                    <Button type="button" variant="outline" data-testid={U.cancel} disabled={blockClose} onClick={() => onOpenChange(false)}>
                        {committed ? t("stk_admin_close") : t("stk_admin_cancel")}
                    </Button>
                    {!committed ? (
                        <Button type="button" data-testid={U.commit} disabled={!canCommit} onClick={commit} className="gap-2">
                            {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            {committing ? t("stk_admin_saving") : t("stk_costs_commit")}
                        </Button>
                    ) : null}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
